<template lang="pug">
  .user-details
    v-row
      v-col(sm="6")
        h2.primary--text {{ title }}

      v-col.text-right(v-if="isEdited" sm="6")
        v-btn.mr-2(v-if="!!id" color="primary" @click="loadUserDetails") Rückgängig
        v-btn(color="primary" @click="onSaveUser") Speichern

    v-dialog(v-model="showPasswordModal" width="450")
      password-form.mt-0(:user-id="showPasswordModal ? id : 0" @saved="showPasswordModal = false")

    v-form(ref="form-user" lazy-validation @submit.prevent="() => {}")
      v-expansion-panels.mt-4(v-model="panel" flat tile multiple)
        v-expansion-panel
          v-expansion-panel-header Benutzerdaten
          v-expansion-panel-content: v-row.my-6
            v-col.pb-0(cols="12" sm="6"): v-text-field(
              v-model="id"
              label="ID"
              type="number"
              outlined
              readonly
            )

            v-col.pb-0(cols="12" sm="6"): v-text-field(
              v-model="user.forename"
              label="Vorname"
              outlined
              :readonly="readonly"
              :rules="[ rules.required ]"
            )

            v-col.pb-0(cols="12" sm="6"): v-text-field(
              v-model="user.lastname"
              label="Nachname"
              outlined
              :readonly="readonly"
              :rules="[ rules.required ]"
            )

            v-col.pb-0(cols="12" sm="6"): v-text-field(
              v-model="user.email"
              label="E-Mail"
              outlined
              :readonly="readonly"
              :rules="[ rules.email, rules.required ]"
            )

            v-col.pb-0(v-if="!isSSO" cols="12" sm="6"): v-select(
              v-model="user.user_group"
              label="Gruppe wählen"
              outlined
              dense
              :items="userGroups"
              :readonly="readonly"
              :rules="[ rules.required ]"
            )

            v-col.pb-0(cols="12" sm="6"): v-text-field(
              v-model="user.mobile_nr"
              label="Mobil-Nr."
              outlined
              :readonly="readonly"
            )

            v-col.pb-0(cols="12" sm="6"): v-textarea(
              v-model="user.contact_data"
              label="Standard-Text Absender (Druckansicht)"
              outlined
              :readonly="readonly"
            )

            v-col.pb-0(cols="12" sm="6")

            // v-col.pb-0(v-if="isAgency" cols="12" sm="6")

            v-col.pb-0(v-if="id && !isSSO" cols="12" sm="6"): v-checkbox.mt-0(
              v-model="user.disabled"
              label="Deaktiviert"
              hide-details
              :readonly="readonly"
            )

            v-col.pb-0(v-if="id && !isSSO" cols="12" sm="6"): v-checkbox.mt-0(
              v-model="user.email_validated"
              label="Email validiert"
              hide-details
              disabled
            )

            v-col.pb-0.mt-4(v-if="id && !isSSO" cols="12")
              v-btn(color="primary" @click="showPasswordModal = true") Passwort ändern

        v-expansion-panel.mt-3(v-if="!isSSO")
          v-expansion-panel-header Agenturen
          v-expansion-panel-content
            v-data-table.my-6(
              v-model="userAgencies"
              v-bind="tableProps"
              :headers="agencyHeaders"
              :items="readonly ? userAgencies : agencies"
              :item-key="`id`"
              :show-select="!readonly"
              :options="defaultPagination"
            )
              tr(
                slot="item"
                slot-scope="props"
                :active="props.isSelected"
                @click="props.select(!props.isSelected)"
              )
                td.text-left(v-if="!readonly")
                  v-checkbox.mt-0(primary hide-details :input-value="props.isSelected")

                td.text-left {{ props.item.name }}

</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator'

import Urls from '@/router/urls'
import FormMixin from '@/mixins/form'

@Component({
  components: {
    PasswordForm: () => import('@/pages/auth/password')
  },

  mixins: [
    FormMixin
  ],

  watch: {
    agencies () {
      this.userAgencies = (this.user.agencies || [])
        .map(id_ => this.agencies.find(a => a.id === id_))
        .filter(a => !!a)
    },

    user: {
      deep: true,
      handler () {
        this.isEdited = true
      }
    },

    userAgencies: {
      deep: true,
      handler () {
        this.isEdited = true
      }
    }
  }
})
export default class UserDetails extends Vue {
  @Prop({ type: [ Number, String ], default: 0 }) id
  @Prop({ type: Boolean, default: false }) readonly

  user = {}
  userAgencies = []

  panel = [0]
  showPasswordModal = false
  isEdited = false

  userGroups = [
    'SuperAdministrator',
    'Administrator',
    'Agentur'
  ]

  agencyHeaders = [
    {
      text: 'Name',
      align: 'left',
      value: 'name',
      sortable: false
    }
  ]

  get isAgency () {
    return this.user && this.user.user_group === 'Agentur'
  }

  get title () {
    if (this.readonly) {
      return 'Mein Profil'
    }

    if (!this.id) {
      return 'Benutzer anlegen'
    }

    return this.user && this.user.id
      ? `Benutzer (${ this.user.forename }/${ this.user.lastname }) bearbeiten`
      : `Benutzer bearbeiten`
  }

  mounted () {
    this.loadUserDetails()
  }

  loadUserDetails () {
    if (!this.id) {
      return
    }

    this.$axios
      .get(Urls.User(this.id))
      .then(res => {
        this.user = res.data
        this.userAgencies = (this.user.agencies || [])
          .map(id_ => this.agencies.find(a => a.id === id_))
          .filter(a => !!a)

        this.$nextTick(() => {
          this.isEdited = false
        })
      })
  }

  handleAddUser (params) {
    this.$axios
      .post(Urls.AdminUser(), params)
      .then(() => {
        this.$toast.success('Daten gespeichert', 'Speichern')
        this.gotoRoute('user-list')
        this.isEdited = false
      })
      .catch(err => {
        this.$toast.error(err.message, 'Fehler')
      })
  }

  handleUpdateUser (params) {
    this.$axios
      .put(Urls.AdminUser(this.id), params)
      .then(() => {
        this.$toast.success('Daten gespeichert', 'Speichern')
        this.isEdited = false
      })
      .catch(err => {
        this.$toast.error(err.message, 'Fehler')
      })
  }

  onSaveUser () {
    if (!this.isFormValid('form-user')) {
      return
    }

    const params = {
      forename: this.user.forename,
      lastname: this.user.lastname,
      email: this.user.email,
      mobile_nr: this.user.mobile_nr,
      contact_data: this.user.contact_data,
      user_group: this.user.user_group,
      agencies: this.userAgencies.map(a => a.id)
    }

    if (this.id) {
      params.disabled = this.user.disabled
      this.handleUpdateUser(params)
    } else {
      this.handleAddUser(params)
    }
  }
}
</script>
